import { useLocation, useNavigate } from 'react-router-dom';

export interface IGetProductURL {
  name: string;
  sku: string;
  urlTemplate: string;
}

/**
 * Generate a slug as similar as possible to the Salesforce
 * rules to create the URLs of each product. So in edge cases
 * some slug may not be exactly the same as the Salesforce ones.
 *
 * @param string - the string to slugify
 * @returns the slugified string
 */
export const slugify = (string: string): string => {
  if (!string) return '';

  const a = 'áâäèéêëïíİñôöòóüú:;';
  const b = 'aaaeeeeiiinoooouu--';
  const p = new RegExp(a.split('').join('|'), 'g');

  return unescape(string)
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/[|]+/g, '-') // Replace escaped pipeline with hyphen
    .replace(
      /[^\w-.·~–“”‘’/{}^àæãåāăąçćčđďēėęěğǵḧîīįìıłḿńǹňœøōõőṕŕřßśšşșťțûùūǘůűųẃẍÿýžźż]+/g,
      ''
    ) // Remove all non-word characters (except some of them)
    .replace(/[/{}^]+/g, (c) => encodeURIComponent(c)) // Encode some characters again
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '') // Trim hyphens from start of text
    .replace(/-+$/, ''); // Trim hyphens from end of text
};

/**
 * Generate a product URL with a slug as similar as possible
 * to the Salesforce rules. So in edge cases some URLs
 * may not be exactly the same as the Salesforce ones.
 *
 * @param name - the name of the product
 * @param sku - the SKU of the product
 * @param urlTemplate - the template where the generated slug will be inserted
 * @returns the URL of the product
 */
export const getProductURL = ({
  name,
  sku,
  urlTemplate
}: IGetProductURL): string => {
  if (!name || !sku || !urlTemplate) return '';
  const slug = slugify(name);
  const skuSplit = sku?.split('-');

  return urlTemplate.replace(':product', `${slug}-${skuSplit?.[0]}`);
};

export const useNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const updateQueryString = (
    queryObject: Record<string, string>,
    urlBase?: string
  ) => {
    const baseUrl = urlBase ?? location.pathname;
    const searchParams = new URLSearchParams(location.search);

    Object.entries(queryObject).forEach(([key, value]) => {
      searchParams.set(key, value);
    });

    const redirectTo = `${baseUrl}?${searchParams.toString()}`;

    navigate(redirectTo, { replace: true });
  };

  const removeQueryString = (keys: string[], urlBase?: string) => {
    const baseUrl = urlBase ?? location.pathname;
    const searchParams = new URLSearchParams(location.search);

    keys.forEach((key) => {
      searchParams.delete(key);
    });

    const redirectTo =
      searchParams.toString() === ''
        ? baseUrl
        : `${baseUrl}?${searchParams.toString()}`;

    navigate(redirectTo, { replace: true });
  };

  return { updateQueryString, removeQueryString };
};
